import { memo, useRef, useCallback, useMemo, useEffect, useState } from 'react';
import PlayCircleOutlinedIcon from '@mui/icons-material/PlayCircleOutlined';
import { IconButton, useTheme, Box, CircularProgress } from '@mui/material';
import ReactPlayer from 'react-player';
import { useCosmosPlayerContext } from 'providers/contexts/CosmosPlayerContext';
import { useNotify } from 'react-admin';
import { CosmosPlayerProps } from './types';
import { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const CosmosPlayer = memo(
  ({
    isTrimMode,
    trimStartTime: propTrimStartTime,
    trimEndTime: propTrimEndTime,
    setTrimStartTime,
    setTrimEndTime,
    setCurrentTime,
    playerMeta,
    autoPlay,
    wrapperSx,
    hidePlayIcon = false,
    onPlayerReady,
    ...props
  }: CosmosPlayerProps) => {
    const { t } = useTranslation();
    const {
      removePlayer,
      addPlayer,
      pauseAllOtherPlayers,
      setCurrentlyPlayingId,
      currentlyPlayingId,
      pauseAllGeneration,
    } = useCosmosPlayerContext();
    const theme = useTheme();
    const notify = useNotify();
    const playerRef = useRef<HTMLVideoElement | null>(null);

    // Track states
    const [playTriggered, setPlayTriggered] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [isBuffering, setIsBuffering] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [errorNotified, setErrorNotified] = useState(false);
    const [isPlaying, setIsPlaying] = useState(autoPlay || false);

    // Local trim state
    // no local currentTime kept; parent may receive updates via setCurrentTime
    // Sync incoming prop-based trim start/end into the player when they change
    useEffect(() => {
      if (typeof propTrimStartTime === 'number') {
        // seek to start when entering trim mode and start changes
        if (isTrimMode && playerRef.current) {
          playerRef.current.currentTime = propTrimStartTime;
        }
      }
    }, [propTrimStartTime, isTrimMode]);

    // Compute if we should show loading overlay
    // Only show loading when play was triggered but content isn't ready yet or is buffering
    // AND there's no error
    const showLoading = playTriggered && (!isReady || isBuffering) && !hasError;

    // Pause this player when another player starts playing.
    // This replaces direct DOM .pause() calls which cause feedback loops in v3.
    useEffect(() => {
      if (
        currentlyPlayingId !== null &&
        currentlyPlayingId !== playerMeta.uniqueId &&
        isPlaying
      ) {
        setIsPlaying(false);
      }
    }, [currentlyPlayingId, playerMeta.uniqueId, isPlaying]);

    // Pause this player when pauseAllPlayers is called (generation increments)
    useEffect(() => {
      if (pauseAllGeneration > 0 && isPlaying) {
        setIsPlaying(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pauseAllGeneration]);

    // When start, or resume playing after pausing or buffering
    const handlePlay = useCallback(() => {
      if (hasError) return; // Don't play if there's an error

      setPlayTriggered(true);
      setIsPlaying(true);
      pauseAllOtherPlayers(playerMeta.uniqueId);
    }, [pauseAllOtherPlayers, playerMeta.uniqueId, hasError]);

    // Handle when player is paused
    const handlePause = useCallback(() => {
      setIsPlaying(false);

      // Only clear currentlyPlayingId if this player is still the active one.
      // Use functional updater to avoid stale closure issues.
      setCurrentlyPlayingId((prevId) => (prevId === playerMeta.uniqueId ? null : prevId));
    }, [playerMeta.uniqueId, setCurrentlyPlayingId]);

    // Handle errors in the media player
    const handleError = useCallback(
      (e: React.SyntheticEvent<HTMLVideoElement | HTMLAudioElement> | string) => {
        // Set all states to stop loading and playing
        setHasError(true);
        setIsReady(true); // Stop showing loading
        setIsBuffering(false);
        setIsPlaying(false);

        // Only notify if we haven't already notified for this error
        if (!errorNotified) {
          let errorMsg = t('blox.video_field_input.error.media_playback.generic'); // Default unknown error message
          let errorType: 'info' | 'success' | 'warning' | 'error' = 'error';
          let notifyDuration = 4000;

          if (typeof e === 'string') {
            errorMsg = e;
          } else if (e && 'currentTarget' in e) {
            const playerElement = e.currentTarget;
            if (playerElement?.error?.code) {
              switch (playerElement.error.code) {
                case 1:
                  errorMsg = t('blox.video_field_input.error.media_playback.aborted');
                  break;
                case 2:
                  errorMsg = t(
                    'blox.video_field_input.error.media_playback.network_error',
                  );
                  break;
                case 3:
                  errorMsg = t(
                    'blox.video_field_input.error.media_playback.decode_error',
                  );
                  break;
                case 4:
                  errorMsg = t(
                    'blox.video_field_input.error.media_playback.format_converting',
                  );
                  errorType = 'info';
                  notifyDuration = 6000;
                  break;
                default:
                  break;
              }
            }
          }

          notify(`${errorMsg}`, { type: errorType, autoHideDuration: notifyDuration });
          setErrorNotified(true);
        }

        // Clear current player on error if this was the current player
        if (currentlyPlayingId === playerMeta.uniqueId) {
          setCurrentlyPlayingId(null);
        }
      },
      [
        notify,
        currentlyPlayingId,
        playerMeta.uniqueId,
        setCurrentlyPlayingId,
        errorNotified,
        t,
      ],
    );

    // Handle end of media playback
    const handleEnded = useCallback(() => {
      setIsPlaying(false);
      setPlayTriggered(false); // Reset play trigger state

      // Clear currently playing ID when media ends (functional updater to avoid stale closure)
      setCurrentlyPlayingId((prevId) => (prevId === playerMeta.uniqueId ? null : prevId));
    }, [playerMeta.uniqueId, setCurrentlyPlayingId]);

    // Handle when the player is ready
    const handleReady = useCallback(() => {
      if (hasError) return; // Skip if there's an error

      setIsReady(true);
      setCurrentlyPlayingId(playerMeta.uniqueId);

      // Notify parent with the player element
      if (onPlayerReady && playerRef.current) {
        onPlayerReady(playerRef.current);
      }
    }, [hasError, setCurrentlyPlayingId, playerMeta.uniqueId, onPlayerReady]);

    // Handle when the light mode play button is clicked
    const handleLightClick = useCallback(() => {
      if (hasError) return; // Don't attempt to play if there's an error

      setPlayTriggered(true);
    }, [hasError]);

    // For trimming videos, don't let the player play past the trim end time.
    const handleTimeUpdate = useCallback(() => {
      if (!playerRef.current) return;
      const currentTime = playerRef.current.currentTime;

      if (isTrimMode) {
        const endTime = propTrimEndTime;

        // update parent currentTime if provided
        setCurrentTime?.(currentTime);

        if (
          isPlaying &&
          typeof endTime === 'number' &&
          !Number.isNaN(endTime) &&
          currentTime >= endTime
        ) {
          if (playerRef.current) {
            playerRef.current.currentTime = endTime;
          }

          setIsPlaying(false);
          setPlayTriggered(false);
        }
      }
    }, [isPlaying, isTrimMode, propTrimEndTime, setCurrentTime]);

    // Register the player on mount
    // Remove from context by id on unmount
    useEffect(() => {
      addPlayer({
        ref: playerRef,
        uid: playerMeta.uniqueId,
        src: playerMeta.source,
        title: playerMeta.title,
        poster: playerMeta.poster,
        alt: playerMeta.alt,
        type: playerMeta.type,
      });
      return () => {
        removePlayer(playerMeta.uniqueId);
      };
    }, [
      addPlayer,
      removePlayer,
      playerMeta.uniqueId,
      playerMeta.source,
      playerMeta.title,
      playerMeta.poster,
      playerMeta.alt,
      playerMeta.type,
    ]);

    // Reset states when video source changes
    useEffect(() => {
      setIsReady(false);
      setPlayTriggered(false);
      setIsBuffering(false);
      setHasError(false);
      setErrorNotified(false);
      setIsPlaying(autoPlay || false);
    }, [playerMeta.source, autoPlay]);

    // Validate source URL
    useEffect(() => {
      if (playerMeta.uniqueId === undefined) return;
      // Basic URL validation - check if it's a string and looks like a URL
      if (
        !playerMeta.source ||
        typeof playerMeta.source !== 'string' ||
        (!playerMeta.source.startsWith('http') && !playerMeta.source.startsWith('blob'))
      ) {
        handleError('Invalid media source URL');
      }
    }, [playerMeta.uniqueId, playerMeta.source, handleError]);

    // Update the video's start time to match to the VideoRangeSlider startTime.
    useEffect(() => {
      if (!isTrimMode) return;
      const startTime = propTrimStartTime ?? 0;
      if (typeof startTime === 'number' && playerRef.current) {
        // ensure seconds mode
        playerRef.current.currentTime = startTime;
      }
    }, [isTrimMode, propTrimStartTime]);

    // Get default light properties based on player type
    const lightProps = useMemo(() => {
      if (props.light === false) return false; // Explicitly disabled
      switch (playerMeta?.type) {
        case 'video': {
          return playerMeta?.poster || playerMeta?.preview || false;
        }
        default: {
          return false;
        }
      }
    }, [playerMeta, props.light]);

    // Get default configuration properties based on player type
    const configProps = useMemo(() => {
      //This check prevents HLS errors with the source is not a streaming source
      let hlsConfig = {};
      if (playerMeta.type === 'video' && playerMeta?.source.endsWith('.m3u8')) {
        hlsConfig = {
          maxBufferSize: 30 * 1000 * 1000, // 30MB (adjust as needed)
          maxBufferLength: 60, // Maximum buffer length in seconds
          backBufferLength: 30, // Reduce back buffer (seconds to keep in buffer)
        };
      }
      return {
        hls: hlsConfig,
        youtube: {
          color: 'white',
        },
      };
    }, [playerMeta.source, playerMeta.type]);

    const boxSx: SxProps<Theme> = useMemo(
      () => ({
        position: 'relative',
        height: '100%',
        ...(typeof wrapperSx === 'function' ? wrapperSx(theme) : wrapperSx),
        '& .react-player__preview': {
          borderRadius: 1,
        },
        '& video, & iframe': {
          borderRadius: 1,
          backgroundColor: theme.blox.colors.black,
        },
      }),
      [theme, wrapperSx],
    );

    if (!playerMeta?.source) {
      return null;
    }

    return (
      <Box
        sx={{
          ...boxSx,
        }}
        id="cosmos-player-wrapper"
        data-testid="cosmos-player-wrapper"
      >
        {showLoading && (
          <Box
            data-testid="loading-overlay"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.blox.colors.black,
              zIndex: 2,
              borderRadius: 1,
            }}
          >
            <CircularProgress
              sx={{ color: theme.blox.colors.white }}
              size={60}
            />
          </Box>
        )}
        <ReactPlayer
          data-testid="cosmos-player"
          ref={playerRef}
          src={playerMeta.source}
          crossOrigin="anonymous"
          onError={(err, data, hls, global) => {
            // Code 4 (MEDIA_ERR_SRC_NOT_SUPPORTED) is expected for formats that
            // require server-side conversion (e.g. MPG, AVI); handleError surfaces
            // a friendly "converting" notification, so don't flood the console.
            const mediaErrorCode =
              err && typeof err === 'object' && 'currentTarget' in err
                ? (err as React.SyntheticEvent<HTMLVideoElement | HTMLAudioElement>)
                    .currentTarget?.error?.code
                : undefined;
            const log = hls || mediaErrorCode === 4 ? console.warn : console.error;
            log('[CosmosPlayer] Error:', { err, data, hls, global });

            // HLS errors thrown are generally informational not breaking
            if (!hls && !errorNotified) {
              handleError(data?.err?.message || err || 'Unknown error');
            }
          }}
          onPlay={handlePlay}
          onReady={handleReady}
          onPause={handlePause}
          onEnded={handleEnded}
          onTimeUpdate={handleTimeUpdate}
          onWaiting={() => !hasError && setIsBuffering(true)}
          onPlaying={() => setIsBuffering(false)}
          controls={props.controls || true}
          light={lightProps}
          config={configProps}
          playing={isPlaying && !hasError} // Stop playing if there's an error
          loop={props.loop || false}
          muted={props.muted || false}
          volume={props.volume || 1}
          playbackRate={props.playbackRate || 1}
          pip={false}
          width={props.width || '100%'}
          height={props.height || '100%'}
          playIcon={
            <IconButton
              aria-label="Play Media"
              onClick={handleLightClick}
              disabled={hasError} // Disable play button if there's an error
              sx={{
                display: hidePlayIcon ? 'none' : 'flex',
              }}
            >
              <PlayCircleOutlinedIcon
                sx={{
                  fontSize: '10rem',
                  opacity: hasError ? 0.3 : 0.5, // Dimmer if there's an error
                  ':hover': { opacity: hasError ? 0.3 : 1 },
                  color: theme.blox.colors.white,
                }}
              />
            </IconButton>
          }
          onClick={(event: any) => {
            event.stopPropagation();
          }}
          {...props}
        />
      </Box>
    );
  },
);

CosmosPlayer.displayName = 'CosmosPlayer';
export default CosmosPlayer;
